import { Button } from "@src/components/ui-components/Buttons/Button/Button";
import { t } from "i18next";
import { useEffect, useState } from "react";
import { LanguageCode, SupportedLanguages } from "../../../libs/i18next/language";
import { TranslationData } from "../../../to-be-removed/LocalData/General/TranslationData";
import { SelectInputData, SelectInputField } from "../SelectInputField/SelectInputField";
import { TextArea } from "../TextArea/TextArea";
import styles from "./TranslationDataEditor.module.css";
import { TextInputField } from "../TextInputField/TextInputField";
import { AriaButton } from "@src/components/ui-components/Buttons/AriaButton/AriaButton";

interface TranslationDataEditorProps {
  /** The TranslationData that can be edited */
  data: TranslationData | undefined;
  /** Textarea */
  inputType?: "input" | "textarea";
  /** A callback that is triggered when a user taps the Save-button on the editor. Saving the data to Firebase needs to be done outside TranslationDataEditor */
  saveTapped?: (standardLangCode: LanguageCode, translation: { [key: string]: any }) => void;
  /** A callback that is triggered when a user edits a value */
  onNameChange?: (standardLangCode: LanguageCode, translation: { [key: string]: any }) => void;
  /** Optional boolean that when true, displays a saving-indicator on the Save-button */
  saving?: boolean;
  placeholder?: string;
}

interface TranslationRow {
  langCode: SupportedLanguages;
  text: string;
}
export const TranslationDataEditor = (props: TranslationDataEditorProps) => {
  const [translations, setTranslations] = useState<TranslationRow[]>([]);

  const hasUniqueLanguages = !translations.find((t, index) =>
    translations.find((t2, index2) => t2.langCode.code === t.langCode.code && index !== index2)
  );
  const canSave =
    props.saveTapped &&
    hasUniqueLanguages &&
    (!props.data ||
      Object.keys(props.data.fields).length !== translations.length ||
      translations.find(
        (row) => row.text !== props.data?.fields[row.langCode.code]?.default || !Object.keys(props.data.fields).includes(row.langCode.code)
      ));

  useEffect(() => {
    if (props.data) {
      let keys = Object.keys(props.data.fields).sort((a) => (a === props.data?.standardLangCode.code ? -1 : 0));
      if (keys.length === 0) keys = [props.data.standardLangCode.code];

      setTranslations(
        keys.map((key) => {
          return { langCode: SupportedLanguages.getLanguageWithCode(key), text: props.data?.fields[key]?.default ?? "" };
        })
      );
    } else {
      setTranslations([{ langCode: SupportedLanguages.ALL_LANGUAGES[0], text: "" }]);
    }
  }, [props.data]);

  useEffect(() => {
    if (props.onNameChange && translations.length > 0) {
      const saveData: { [key: string]: any } = {};
      for (const translation of translations) {
        saveData[translation.langCode.code] = {
          default: translation.text,
        };
      }
      props.onNameChange(translations[0].langCode.code, saveData);
    }
  }, [translations]);

  function save() {
    if (canSave && !props.saving) {
      const saveData: { [key: string]: any } = {};
      for (const translation of translations) {
        saveData[translation.langCode.code] = {
          default: translation.text,
        };
      }

      props.saveTapped?.(translations[0].langCode.code, saveData);
    }
  }

  const translationToAdd = SupportedLanguages.ALL_LANGUAGES.find(
    (lang) => !translations.find((translation) => translation.langCode.code === t("main.code"))
  );

  return (
    <table className={styles.table} onClick={(e) => e.stopPropagation()}>
      <tbody>
        {translations.map((translation, index) => {
          return (
            <tr key={translation.langCode.code + index}>
              <td>
                {props.inputType === "textarea" ? (
                  <TextArea
                    value={translation.text}
                    onChange={(text) => {
                      const newTranslations = [...translations];
                      newTranslations[index].text = text;
                      setTranslations(newTranslations);
                    }}
                    width="100%"
                    placeholder={props.placeholder ?? t("main.fillInContent")}
                  />
                ) : (
                  <TextInputField
                    value={translation.text}
                    onChange={(text) => {
                      const newTranslations = [...translations];
                      newTranslations[index].text = text;
                      setTranslations(newTranslations);
                    }}
                    placeholder={props.placeholder ?? t("main.fillInName")}
                    width="100%"
                  />
                )}
              </td>
              <td>
                <SelectInputField
                  options={SupportedLanguages.ALL_LANGUAGES.map((type) => new SelectInputData(type.code, type.flag + " " + type.code))}
                  value={translation.langCode.code}
                  onChange={(selectedOption) => {
                    if (selectedOption) {
                      const newTranslations = [...translations];
                      newTranslations[index].langCode = SupportedLanguages.getLanguageWithCode(selectedOption?.value);
                      setTranslations(newTranslations);
                    }
                  }}
                />
              </td>
            </tr>
          );
        })}
        {translationToAdd && hasUniqueLanguages && (
          <tr>
            <td>
              <div>
                <Button
                  type="text"
                  size="sm"
                  color="var(--primary-color)"
                  onClick={() => setTranslations([...translations, { langCode: translationToAdd, text: "" }])}
                  title={"+ " + t("main.addTranslation")}
                />
              </div>
            </td>
            <td></td>
          </tr>
        )}
        {canSave && (
          <tr>
            <td>
              <AriaButton size="sm" isLoading={props.saving} onPress={save}>{t("main.save")}</AriaButton> 
            </td>
            <td></td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default TranslationDataEditor;
